import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import liff from '@line/liff';
import { useLiff } from '@/hooks/useLiff';
import { authLine, ApiError } from '@/services';
import { Button, Loading, ErrorMessage, Card } from '@/components/common';
import { LINE_ERROR_MESSAGES, type LineErrorCode } from '@/types/line';
import { getFlaroAppUrl, isValidEnvironment, getEnvironmentFromUrl, type ApiEnvironment } from '@/config/apiDomains';
import { isDebugMode, addLog, getLogs, clearLogs, getPlatformInfo, copyLogsToClipboard } from '@/utils/debugLogger';
import type { DebugLogEntry } from '@/utils/debugLogger';

/** SSO redirectフラグの有効期限（5分） */
const SSO_REDIRECT_TTL_MS = 5 * 60 * 1000;
const SSO_REDIRECT_KEY = 'flaro_sso_redirected';

// リダイレクトフラグの保存（localStorage + Cookie の両方に保存）
// Android LINE WebViewではlocalStorage/sessionStorageがインスタンス再生成で失われる場合がある。
// Cookieは異なる永続化メカニズム（システムレベルのcookie jar）を使用するため、
// WebView再生成後も保持される可能性が高い。
function setSsoRedirectFlag(): void {
  const now = Date.now().toString();
  try { localStorage.setItem(SSO_REDIRECT_KEY, now); } catch { /* ignore */ }
  try { document.cookie = `${SSO_REDIRECT_KEY}=${now}; max-age=300; path=/; SameSite=Lax`; } catch { /* ignore */ }
}

// リダイレクトフラグの読み取りとクリア（localStorage または Cookie のいずれかにあれば有効）
function checkAndClearSsoRedirectFlag(): boolean {
  const now = Date.now();
  let found = false;

  // localStorage チェック
  try {
    const raw = localStorage.getItem(SSO_REDIRECT_KEY);
    if (raw) {
      localStorage.removeItem(SSO_REDIRECT_KEY);
      const ts = parseInt(raw, 10);
      if (!isNaN(ts) && (now - ts) < SSO_REDIRECT_TTL_MS) {
        found = true;
      }
    }
  } catch { /* ignore */ }

  // Cookie チェック
  try {
    const match = document.cookie.match(new RegExp(`${SSO_REDIRECT_KEY}=(\\d+)`));
    if (match) {
      // Cookie クリア
      document.cookie = `${SSO_REDIRECT_KEY}=; max-age=0; path=/; SameSite=Lax`;
      const ts = parseInt(match[1], 10);
      if (!isNaN(ts) && (now - ts) < SSO_REDIRECT_TTL_MS) {
        found = true;
      }
    }
  } catch { /* ignore */ }

  return found;
}

type InternalState =
  | 'idle'
  | 'authenticating'
  | 'redirecting'
  | 'error';

type EffectiveState =
  | 'initializing'
  | 'login_required'
  | 'authenticating'
  | 'redirecting'
  | 'error';

interface PageError {
  title: string;
  message: string;
  canRetry: boolean;
}

export const SsoPage = () => {
  const [searchParams] = useSearchParams();
  const liffHook = useLiff();

  // リダイレクト済みフラグ（ページリロード時の認証ループ防止）
  // Android: liff.closeWindow()が動作しないため、ユーザーがLINEに戻ると
  // LIFFページが再マウントされる。localStorage + Cookieの両方で検出し再認証をスキップする。
  const [redirectCompleted] = useState(() => checkAndClearSsoRedirectFlag());
  const [internalState, setInternalState] = useState<InternalState>(() =>
    redirectCompleted ? 'redirecting' : 'idle'
  );
  const [error, setError] = useState<PageError | null>(null);
  const [debugLogs, setDebugLogs] = useState<DebugLogEntry[]>([]);
  const debugMode = useMemo(() => isDebugMode(), []);
  const [pendingRedirect, setPendingRedirect] = useState<{
    url: string;
    isInClient: boolean;
    reason: 'debug' | 'android_manual';
  } | null>(null);

  const env = useMemo(() => {
    const fromUrl = searchParams.get('env') as ApiEnvironment | null;
    if (fromUrl) return fromUrl;
    return getEnvironmentFromUrl() as ApiEnvironment | null;
  }, [searchParams]);

  // 外部ブラウザ遷移後にLIFF画面を自動的に閉じる
  // liff.closeWindow()はstateを更新せずウィンドウを閉じるだけのため、
  // アンマウント時のクリーンアップは不要
  const scheduleCloseWindow = useCallback(() => {
    setTimeout(() => {
      try {
        liff.closeWindow();
      } catch {
        // closeWindowが失敗した場合はユーザーが手動で閉じる
      }
    }, 300);
  }, []);

  // 有効な状態を計算（LIFFの状態とコンポーネントの状態から派生）
  const effectiveState = useMemo((): EffectiveState => {
    // LIFF読み込み中
    if (liffHook.isLoading) {
      return 'initializing';
    }

    // LIFFエラー
    if (liffHook.error) {
      return 'error';
    }

    // LIFF未初期化
    if (!liffHook.isInitialized) {
      return 'initializing';
    }

    // 未ログイン
    if (!liffHook.isLoggedIn) {
      return 'login_required';
    }

    // コンポーネント内部状態をそのまま返す
    if (internalState === 'idle') {
      return 'authenticating'; // idle状態の場合は認証開始前
    }

    return internalState as EffectiveState;
  }, [liffHook.isLoading, liffHook.error, liffHook.isInitialized, liffHook.isLoggedIn, internalState]);

  useEffect(() => {
    addLog('SsoPage', 'state_change', {
      effectiveState,
      internalState,
      env: env ?? 'none',
      isInitialized: liffHook.isInitialized,
      isLoggedIn: liffHook.isLoggedIn,
      isInClient: liffHook.isInClient,
    });
  }, [effectiveState, internalState, env, liffHook.isInitialized, liffHook.isLoggedIn, liffHook.isInClient]);

  useEffect(() => {
    if (!debugMode) return;
    const refresh = () => setDebugLogs(getLogs());
    refresh();
    const interval = setInterval(refresh, 1000);
    return () => clearInterval(interval);
  }, [debugMode]);

  // 表示用エラー（LIFFエラーまたはコンポーネントエラー）
  const displayError = useMemo((): PageError | null => {
    if (liffHook.error) {
      return {
        title: 'LIFF初期化エラー',
        message: liffHook.error.message,
        canRetry: true,
      };
    }
    return error;
  }, [liffHook.error, error]);

  // FLARO Webへリダイレクト
  const redirectToFlaroWeb = useCallback((authResponse: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
    email: string;
    company_code: string;
  }) => {
    const flaroUrl = getFlaroAppUrl(env, authResponse.company_code);
    const callbackUrl = new URL(`${flaroUrl}/auth/line-callback`);

    // フラグメントでトークンを渡す（サーバーに送信されない）
    // 重要: emailはstaff.mail（FLAROに登録されたメールアドレス）
    const fragment = new URLSearchParams({
      access_token: authResponse.access_token,
      id_token: authResponse.id_token,
      refresh_token: authResponse.refresh_token,
      expires_in: authResponse.expires_in.toString(),
      email: authResponse.email,
    }).toString();

    callbackUrl.hash = fragment;

    const redirectUrl = callbackUrl.toString();
    const isInClient = liff.isInClient();

    addLog('SsoPage', 'redirect_to_flaro', {
      flaroUrl: callbackUrl.origin + callbackUrl.pathname,
      isInClient,
    });

    // デバッグモード時はリダイレクトを一時停止してログ確認可能にする
    if (debugMode) {
      addLog('SsoPage', 'redirect_paused_for_debug', { redirectUrl: callbackUrl.origin + callbackUrl.pathname, isInClient });
      setPendingRedirect({ url: redirectUrl, isInClient, reason: 'debug' });
      return;
    }

    // 履歴を置換してセキュリティを強化
    window.history.replaceState({}, document.title, window.location.pathname);

    // LINEアプリ内の場合は外部ブラウザで開く
    if (isInClient) {
      const os = liff.getOS?.() ?? 'unknown';
      const displayUrl = callbackUrl.origin + callbackUrl.pathname;
      addLog('SsoPage', 'opening_external_browser', { url: displayUrl, os });

      if (os === 'android') {
        // Android: 自動リダイレクトするとliff.closeWindow()が動作しないため
        // LINEに戻った際に認証ループが発生する。手動ボタンで遷移させることでループを防止する。
        addLog('SsoPage', 'android_manual_redirect_pending', { url: displayUrl });
        setSsoRedirectFlag();
        setPendingRedirect({ url: redirectUrl, isInClient: true, reason: 'android_manual' });
      } else {
        // iOS/その他: liff.openWindowは正常動作する
        try {
          setSsoRedirectFlag();
          liff.openWindow({
            url: redirectUrl,
            external: true,
          });
          addLog('SsoPage', 'openWindow_called', { os });
          scheduleCloseWindow();
        } catch (openError) {
          addLog('SsoPage', 'openWindow_exception', {
            error: openError instanceof Error ? openError.message : String(openError),
            os,
          });
        }
      }
    } else {
      window.location.href = redirectUrl;
    }
  }, [env, debugMode, scheduleCloseWindow]);

  // LINE認証を実行
  const authenticate = useCallback(async () => {
    addLog('SsoPage', 'authenticate_start', {
      hasAccessToken: !!liffHook.accessToken,
      env: env ?? 'none',
    });

    if (!liffHook.accessToken) {
      setError({
        title: '認証エラー',
        message: 'LINEアクセストークンが取得できませんでした。再度お試しください。',
        canRetry: true,
      });
      setInternalState('error');
      return;
    }

    // 環境パラメータの検証（オプショナル）
    if (env && !isValidEnvironment(env)) {
      setError({
        title: '環境パラメータエラー',
        message: 'URLの環境パラメータが無効です。正しいURLを使用してください。',
        canRetry: false,
      });
      setInternalState('error');
      return;
    }

    setInternalState('authenticating');

    try {
      const result = await authLine({
        line_access_token: liffHook.accessToken,
        line_id_token: liffHook.idToken || undefined,
      });

      setInternalState('redirecting');
      addLog('SsoPage', 'authenticate_success', {
        // companyCodeはプライバシー保護のためマスク（先頭2文字のみ表示）
        companyCode: result.company_code ? `${result.company_code.slice(0, 2)}***` : 'none',
        hasTokens: !!(result.access_token && result.id_token),
      });
      redirectToFlaroWeb(result);
    } catch (err) {
      if (err instanceof ApiError) {
        const errorCode = err.code as LineErrorCode;
        const message = LINE_ERROR_MESSAGES[errorCode] || err.message;
        setError({
          title: 'ログインに失敗しました',
          message,
          canRetry: !['LINE_NOT_LINKED', 'COGNITO_USER_NOT_FOUND'].includes(errorCode),
        });
      } else {
        setError({
          title: 'エラーが発生しました',
          message: 'ログイン中にエラーが発生しました。もう一度お試しください。',
          canRetry: true,
        });
      }
      addLog('SsoPage', 'authenticate_error', {
        errorType: err instanceof ApiError ? 'ApiError' : 'Unknown',
        errorCode: err instanceof ApiError ? err.code : undefined,
        message: err instanceof Error ? err.message : String(err),
      });
      setInternalState('error');
    }
  }, [liffHook.accessToken, liffHook.idToken, env, redirectToFlaroWeb]);

  // LIFF初期化後の処理（ログイン済みの場合のみ認証を開始）
  useEffect(() => {
    // まだ準備ができていない場合は何もしない
    if (liffHook.isLoading || liffHook.error || !liffHook.isInitialized || !liffHook.isLoggedIn) {
      return;
    }

    // リダイレクト済みの場合は再認証せずLIFF画面を閉じる
    if (redirectCompleted) {
      addLog('SsoPage', 'redirect_already_completed', {});
      scheduleCloseWindow();
      return;
    }

    // idle状態の場合のみauthenticateを呼び出す
    if (internalState === 'idle') {
      queueMicrotask(() => {
        authenticate();
      });
    }
  }, [liffHook.isLoading, liffHook.error, liffHook.isInitialized, liffHook.isLoggedIn, internalState, authenticate, redirectCompleted, scheduleCloseWindow]);

  // LINEログイン
  const handleLogin = () => {
    addLog('SsoPage', 'login_button_clicked', {
      currentUrl: window.location.href,
    });
    liffHook.login();
  };

  // リトライ
  const handleRetry = () => {
    addLog('SsoPage', 'retry_clicked', {});
    setError(null);
    setInternalState('idle');
  };

  // デバッグモードで保留中のリダイレクトを実行
  const handleExecuteRedirect = () => {
    if (!pendingRedirect) return;
    addLog('SsoPage', 'redirect_manual_execute', {
      url: pendingRedirect.url.split('#')[0],
      isInClient: pendingRedirect.isInClient,
    });
    window.history.replaceState({}, document.title, window.location.pathname);
    if (pendingRedirect.isInClient) {
      const os = liff.getOS?.() ?? 'unknown';
      const displayUrl = pendingRedirect.url.split('#')[0];
      addLog('SsoPage', 'opening_external_browser_manual', { url: displayUrl, os });

      if (os === 'android') {
        // Android: liff.openWindowはLINE内蔵ブラウザを開くため使用しない
        // Intent URLでChromeを直接起動する
        try {
          const urlObj = new URL(pendingRedirect.url);
          const fragment = urlObj.hash ? urlObj.hash.substring(1) : '';
          const intentBase = `${urlObj.host}${urlObj.pathname}`;
          const intentQuery = fragment
            ? `?_f=${encodeURIComponent(fragment)}`
            : urlObj.search || '';
          const intentUrl = `intent://${intentBase}${intentQuery}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;end`;

          addLog('SsoPage', 'android_intent_url_direct_manual', { intentBase, hasFragment: !!fragment });
          setSsoRedirectFlag();
          window.location.href = intentUrl;
          addLog('SsoPage', 'android_intent_url_dispatched_manual', {});
          setPendingRedirect(null);
          scheduleCloseWindow();
        } catch (intentError) {
          addLog('SsoPage', 'android_intent_url_error_manual', {
            error: intentError instanceof Error ? intentError.message : String(intentError),
          });
        }
      } else {
        // iOS/その他: liff.openWindowは正常動作する
        try {
          setSsoRedirectFlag();
          liff.openWindow({ url: pendingRedirect.url, external: true });
          addLog('SsoPage', 'openWindow_called_manual', { os });
          setPendingRedirect(null);
          scheduleCloseWindow();
        } catch (openError) {
          addLog('SsoPage', 'openWindow_exception_manual', {
            error: openError instanceof Error ? openError.message : String(openError),
            os,
          });
        }
      }
    } else {
      window.location.href = pendingRedirect.url;
    }
  };

  // 閉じる
  const handleClose = () => {
    liffHook.closeLiff();
  };

  // ログイン画面に戻る（LINEログアウト→ページリロードで login_required 状態に遷移）
  const handleBackToLogin = () => {
    addLog('SsoPage', 'back_to_login_clicked', {});
    try {
      liffHook.logout();
    } catch {
      // logout内でリロードされるが、万一例外がスローされた場合のフォールバック
      window.location.reload();
    }
  };

  // レンダリング
  const renderContent = () => {
    switch (effectiveState) {
      case 'initializing':
        return (
          <Loading
            fullScreen
            message="LINEと接続中..."
          />
        );

      case 'login_required':
        return (
          <Card className="text-center">
            <div className="space-y-6">
              <div>
                <div className="w-20 h-20 bg-line/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-line" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.064-.021.136-.031.2-.031.211 0 .391.09.51.25l2.437 3.311V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">LINEログインが必要です</h2>
                <p className="text-gray-500 mt-2">
                  FLAROにログインするにはLINEアカウントでログインしてください
                </p>
              </div>
              <Button variant="line" fullWidth onClick={handleLogin}>
                LINEでログイン
              </Button>
            </div>
          </Card>
        );

      case 'authenticating':
        return (
          <Loading
            fullScreen
            message="FLAROにログイン中..."
          />
        );

      case 'redirecting':
        if (pendingRedirect) {
          if (pendingRedirect.reason === 'debug') {
            return (
              <Card>
                <div className="text-center space-y-4">
                  <div className="text-green-600 font-bold text-lg">認証成功 - リダイレクト一時停止中</div>
                  <p className="text-sm text-gray-600">
                    デバッグモードのためリダイレクトを停止しています。<br />
                    ログを確認・コピーしてから手動でリダイレクトしてください。
                  </p>
                  <div className="bg-gray-50 p-3 rounded text-xs text-left break-all">
                    <div><span className="text-gray-500">リダイレクト先:</span> {pendingRedirect.url.split('#')[0]}</div>
                    <div><span className="text-gray-500">isInClient:</span> {String(pendingRedirect.isInClient)}</div>
                  </div>
                  <Button variant="primary" fullWidth onClick={handleExecuteRedirect}>
                    FLAROへリダイレクトを実行
                  </Button>
                </div>
              </Card>
            );
          }
          // Android in-client: 手動遷移ボタン
          return (
            <Card className="text-center">
              <div className="space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">認証が完了しました</h2>
                  <p className="text-gray-500 text-sm mt-1">
                    下のボタンをタップしてFLAROを開いてください
                  </p>
                </div>
                <Button variant="primary" fullWidth onClick={handleExecuteRedirect}>
                  FLAROを開く
                </Button>
                <Button variant="secondary" fullWidth onClick={handleClose}>
                  この画面を閉じる
                </Button>
              </div>
            </Card>
          );
        }
        if (redirectCompleted) {
          return (
            <Card className="text-center">
              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  FLAROへのログインが完了しました。<br />
                  この画面を閉じてください。
                </p>
                <Button variant="secondary" fullWidth onClick={handleClose}>
                  この画面を閉じる
                </Button>
              </div>
            </Card>
          );
        }
        return (
          <Loading
            fullScreen
            message="FLAROアプリに移動中..."
          />
        );

      case 'error':
        return (
          <Card>
            <ErrorMessage
              title={displayError?.title || 'エラー'}
              message={displayError?.message || 'エラーが発生しました'}
            >
              <div className="flex gap-3 mt-4">
                {displayError?.canRetry && (
                  <Button variant="primary" size="sm" onClick={handleRetry}>
                    再試行
                  </Button>
                )}
                <Button variant="secondary" size="sm" onClick={handleBackToLogin}>
                  ログイン画面に戻る
                </Button>
              </div>
            </ErrorMessage>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* ヘッダー */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">FLARO ログイン</h1>
          <p className="text-sm text-gray-500 mt-1">
            LINEアカウントでFLAROにログインします
          </p>
        </div>

        {/* コンテンツ */}
        {renderContent()}

        {/* デバッグパネル */}
        {debugMode && (
          <div className="mt-6 border-2 border-yellow-400 rounded-lg bg-yellow-50 p-4 text-xs">
            <div className="font-bold text-yellow-800 mb-3 flex items-center gap-2">
              <span>🔧 DEBUG MODE</span>
            </div>

            {/* Platform Info */}
            <details className="mb-3">
              <summary className="font-semibold text-yellow-700 cursor-pointer">Platform Info</summary>
              <pre className="mt-1 bg-white p-2 rounded border overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(getPlatformInfo(), null, 2)}
              </pre>
            </details>

            {/* Current State */}
            <details className="mb-3" open>
              <summary className="font-semibold text-yellow-700 cursor-pointer">Current State</summary>
              <div className="mt-1 bg-white p-2 rounded border space-y-1">
                <div><span className="text-gray-500">effectiveState:</span> <span className="font-mono">{effectiveState}</span></div>
                <div><span className="text-gray-500">internalState:</span> <span className="font-mono">{internalState}</span></div>
                <div><span className="text-gray-500">env:</span> <span className="font-mono">{env ?? 'none'}</span></div>
                <div><span className="text-gray-500">isInitialized:</span> <span className="font-mono">{String(liffHook.isInitialized)}</span></div>
                <div><span className="text-gray-500">isLoggedIn:</span> <span className="font-mono">{String(liffHook.isLoggedIn)}</span></div>
                <div><span className="text-gray-500">isInClient:</span> <span className="font-mono">{String(liffHook.isInClient)}</span></div>
              </div>
            </details>

            {/* Event Logs */}
            <details className="mb-3" open>
              <summary className="font-semibold text-yellow-700 cursor-pointer">Event Logs ({debugLogs.length} entries)</summary>
              <div className="mt-1 bg-white p-2 rounded border max-h-60 overflow-y-auto">
                {debugLogs.length === 0 ? (
                  <div className="text-gray-400">No logs yet</div>
                ) : (
                  debugLogs.map((log, i) => (
                    <div key={i} className="border-b last:border-b-0 py-1">
                      <div className="flex gap-2">
                        <span className="text-gray-400 shrink-0">{new Date(log.timestamp).toLocaleTimeString()}</span>
                        <span className="text-blue-600 font-mono">{log.page}</span>
                        <span className="text-green-700 font-semibold">{log.event}</span>
                      </div>
                      {log.data && (
                        <pre className="text-gray-600 ml-4 whitespace-pre-wrap break-all">
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))
                )}
              </div>
            </details>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => { clearLogs(); setDebugLogs([]); }}
                className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs"
              >
                ログクリア
              </button>
              <button
                onClick={async () => {
                  const ok = await copyLogsToClipboard();
                  alert(ok ? 'ログをクリップボードにコピーしました' : 'コピーに失敗しました');
                }}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs"
              >
                ログコピー
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SsoPage;
